import { useState } from 'react';
import { Plus, Eye, EyeOff, Copy, Trash2, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface EnvironmentVariable {
  id: string;
  key: string;
  value: string;
  type: 'string' | 'secret' | 'number' | 'boolean';
  description?: string;
  isSecret: boolean;
  lastModified: string;
}

interface EnvironmentVariablesProps {
  botId: string;
}

// Mock environment variables
const mockEnvVars: EnvironmentVariable[] = [
  {
    id: '1',
    key: 'OPENAI_API_KEY',
    value: 'sk-proj-abc123...',
    type: 'secret',
    description: 'OpenAI API key for GPT models',
    isSecret: true,
    lastModified: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    key: 'VAPI_API_KEY',
    value: 'vapi_abc123...',
    type: 'secret',
    description: 'Vapi API key for voice calls',
    isSecret: true,
    lastModified: '2024-01-15T10:30:00Z'
  },
  {
    id: '3',
    key: 'MAX_CALL_DURATION',
    value: '300',
    type: 'number',
    description: 'Maximum call duration in seconds',
    isSecret: false,
    lastModified: '2024-01-14T14:22:00Z'
  },
  {
    id: '4',
    key: 'DEBUG_MODE',
    value: 'false',
    type: 'boolean',
    description: 'Enable debug logging',
    isSecret: false,
    lastModified: '2024-01-13T09:15:00Z'
  },
  {
    id: '5',
    key: 'WEBHOOK_URL',
    value: 'https://api.company.com/webhooks/bot-events',
    type: 'string',
    description: 'Webhook endpoint for bot events',
    isSecret: false,
    lastModified: '2024-01-12T16:45:00Z'
  }
];

export function EnvironmentVariables({ botId }: EnvironmentVariablesProps) {
  const [envVars, setEnvVars] = useState(mockEnvVars);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingVar, setEditingVar] = useState<EnvironmentVariable | null>(null);
  const [newVar, setNewVar] = useState({
    key: '',
    value: '',
    type: 'string' as const,
    description: '',
    isSecret: false
  });

  const toggleSecretVisibility = (id: string) => {
    setVisibleSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    console.log('Copied to clipboard');
  };

  const handleAddVariable = () => {
    const id = Date.now().toString();
    const variable: EnvironmentVariable = {
      id,
      key: newVar.key,
      value: newVar.value,
      type: newVar.type,
      description: newVar.description,
      isSecret: newVar.isSecret,
      lastModified: new Date().toISOString()
    };

    setEnvVars(prev => [...prev, variable]);
    setNewVar({
      key: '',
      value: '',
      type: 'string',
      description: '',
      isSecret: false
    });
    setIsAddDialogOpen(false);
  };

  const handleEditVariable = (variable: EnvironmentVariable) => {
    setEditingVar(variable);
    setNewVar({
      key: variable.key,
      value: variable.value,
      type: variable.type,
      description: variable.description || '',
      isSecret: variable.isSecret
    });
    setIsAddDialogOpen(true);
  };

  const handleUpdateVariable = () => {
    if (!editingVar) return;

    setEnvVars(prev => prev.map(v => 
      v.id === editingVar.id 
        ? {
            ...v,
            key: newVar.key,
            value: newVar.value,
            type: newVar.type,
            description: newVar.description,
            isSecret: newVar.isSecret,
            lastModified: new Date().toISOString()
          }
        : v
    ));

    setEditingVar(null);
    setNewVar({
      key: '',
      value: '',
      type: 'string',
      description: '',
      isSecret: false
    });
    setIsAddDialogOpen(false);
  };

  const handleDeleteVariable = (id: string) => {
    setEnvVars(prev => prev.filter(v => v.id !== id));
  };

  const formatValue = (variable: EnvironmentVariable) => {
    if (variable.isSecret && !visibleSecrets.has(variable.id)) {
      return '••••••••••••••••';
    }
    return variable.value;
  };

  const getTypeBadgeVariant = (type: string) => {
    const variants = {
      string: 'default',
      secret: 'destructive',
      number: 'secondary',
      boolean: 'outline'
    };
    return variants[type as keyof typeof variants] || 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Environment Variables</h3>
          <p className="text-sm text-muted-foreground">
            Manage bot environment configuration
          </p>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Add Variable
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingVar ? 'Edit Environment Variable' : 'Add Environment Variable'}
              </DialogTitle>
              <DialogDescription>
                {editingVar 
                  ? 'Update the environment variable details.'
                  : 'Add a new environment variable for this bot.'
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="key">Key</Label>
                <Input
                  id="key"
                  value={newVar.key}
                  onChange={(e) => setNewVar(prev => ({ ...prev, key: e.target.value }))}
                  placeholder="VARIABLE_NAME"
                />
              </div>
              
              <div>
                <Label htmlFor="value">Value</Label>
                <Input
                  id="value"
                  type={newVar.isSecret ? 'password' : 'text'}
                  value={newVar.value}
                  onChange={(e) => setNewVar(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Variable value"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select 
                    value={newVar.type} 
                    onValueChange={(value) => setNewVar(prev => ({ 
                      ...prev, 
                      type: value as any,
                      isSecret: value === 'secret'
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="string">String</SelectItem>
                      <SelectItem value="secret">Secret</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="boolean">Boolean</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  value={newVar.description}
                  onChange={(e) => setNewVar(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Variable description"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setEditingVar(null);
                  setNewVar({
                    key: '',
                    value: '',
                    type: 'string',
                    description: '',
                    isSecret: false
                  });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={editingVar ? handleUpdateVariable : handleAddVariable}
                disabled={!newVar.key || !newVar.value}
              >
                {editingVar ? 'Update' : 'Add'} Variable
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Environment Variables Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Variables ({envVars.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Last Modified</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {envVars.map((variable) => (
                <TableRow key={variable.id}>
                  <TableCell className="font-mono text-sm font-medium">
                    {variable.key}
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded max-w-[200px] truncate">
                        {formatValue(variable)}
                      </code>
                      {variable.isSecret && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleSecretVisibility(variable.id)}
                        >
                          {visibleSecrets.has(variable.id) ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(variable.value)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant={getTypeBadgeVariant(variable.type) as any} className="text-xs">
                      {variable.type}
                    </Badge>
                  </TableCell>
                  
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {variable.description || 'No description'}
                  </TableCell>
                  
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(variable.lastModified)}
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => handleEditVariable(variable)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteVariable(variable.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {envVars.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No environment variables configured for this bot.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}